import { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { allPaymentTypes, enumPaymentTypes, DISCOUNT } from '../../consts/paymentTypes';
import { formatPrice, getTotalOwedTextColor } from '../../helpers/common';
import { enumStatuses, OPEN } from '../../consts/status';

const toDateInputValue = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toISOString().split('T')[0];
};

const buildRows = (selectedPOs) =>
    selectedPOs.flatMap((po) =>
        (po.payments || []).map((payment) => ({
            paymentId: payment.id,
            poId: po.id,
            amount: String(payment.amount ?? ''),
            type: payment.type,
            date: toDateInputValue(payment.date),
            transactionId: payment.transactionId || '',
            note: payment.note || '',
            initial: {
                amount: String(payment.amount ?? ''),
                type: payment.type,
                date: toDateInputValue(payment.date),
                transactionId: payment.transactionId || '',
                note: payment.note || ''
            }
        }))
    );

const rowIsDirty = (row) =>
    row.amount !== row.initial.amount ||
    row.type !== row.initial.type ||
    row.date !== row.initial.date ||
    row.transactionId !== row.initial.transactionId ||
    row.note !== row.initial.note;

/**
 * Bulk Edit Payments dialog (Manus prototype follow-up to PR #219).
 * Lists every recorded payment of the selected POs grouped per PO and lets
 * the user edit them inline. Only changed payments are persisted — each via
 * the existing add-payment upsert endpoint (payment id included).
 */
export const BulkEditPaymentsDialog = ({
    open,
    selectedPOs,
    onSubmit,
    onClose,
    testId
}) => {
    const [rows, setRows] = useState([]);

    useEffect(() => {
        if (open) setRows(buildRows(selectedPOs.filter((po) => po.status === OPEN)));
    }, [open, selectedPOs]);

    const poById = useMemo(
        () => Object.fromEntries(selectedPOs.map((po) => [po.id, po])),
        [selectedPOs]
    );
    const blockedPOs = selectedPOs.filter((po) => po.status !== OPEN);
    const blockedMessage = blockedPOs.length > 0
        ? `${blockedPOs.map((po) => `${po.poNumber || po.id} (${enumStatuses[po.status] || po.status || 'Unknown'})`).join(', ')} can't take payments — set them to Open first.`
        : '';

    if (!open) return null;

    const dirtyRows = rows.filter(rowIsDirty);
    const paymentsCount = rows.length;
    const posCount = selectedPOs.length;

    const updateRow = (paymentId, changes) =>
        setRows((prev) => prev.map((row) => (row.paymentId === paymentId ? { ...row, ...changes } : row)));

    const handleSaveClick = () => {
        const openDirtyRows = dirtyRows.filter((row) => poById[row.poId]?.status === OPEN);
        if (openDirtyRows.length === 0) {
            if (blockedPOs.length > 0) {
                toast.error(blockedMessage || 'Please set this PO to Open to edit a payment.', { position: 'bottom-right' });
            } else {
                toast.info('No payment changes to save.', { position: 'bottom-right' });
            }
            return;
        }
        for (const row of openDirtyRows) {
            const amount = parseFloat(row.amount);
            const poNumber = poById[row.poId]?.poNumber || row.poId;
            if (!row.amount || Number.isNaN(amount)) {
                toast.error(`Enter a valid amount for ${poNumber}`, { position: 'bottom-right' });
                return;
            }
            if (row.type === DISCOUNT && amount >= 0) {
                toast.error(`Discount amount must be less than 0 for ${poNumber}`, { position: 'bottom-right' });
                return;
            }
        }
        onSubmit(openDirtyRows);
    };

    const renderRowFields = (row, compact) => (
        <>
            <select
                value={row.type}
                onChange={(e) => updateRow(row.paymentId, { type: e.target.value })}
                className={`${compact ? 'w-full' : 'w-32'} text-sm border-gray-300 rounded-md`}
                aria-label="Payment type"
            >
                {allPaymentTypes.map((option) => (
                    <option key={option} value={option}>{enumPaymentTypes[option]}</option>
                ))}
            </select>
            <input
                type="date"
                value={row.date}
                onChange={(e) => updateRow(row.paymentId, { date: e.target.value })}
                className={`${compact ? 'w-full' : 'w-36'} text-sm border-gray-300 rounded-md`}
                aria-label="Payment date"
            />
            <div className={`relative ${compact ? 'w-full' : 'w-28'}`}>
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input
                    type="number"
                    step="0.01"
                    value={row.amount}
                    onChange={(e) => updateRow(row.paymentId, { amount: e.target.value })}
                    className="w-full pl-5 text-sm border-gray-300 rounded-md"
                    aria-label="Payment amount"
                />
            </div>
            <input
                type="text"
                value={row.transactionId}
                placeholder="Transaction #"
                onChange={(e) => updateRow(row.paymentId, { transactionId: e.target.value })}
                className={`${compact ? 'w-full' : 'w-40'} text-sm border-gray-300 rounded-md`}
                aria-label="Transaction ID"
            />
            <input
                type="text"
                value={row.note}
                placeholder="Note"
                onChange={(e) => updateRow(row.paymentId, { note: e.target.value })}
                className={`${compact ? 'w-full' : 'flex-1 min-w-24'} text-sm border-gray-300 rounded-md`}
                aria-label="Note"
            />
        </>
    );

    return (
        <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center overflow-y-auto p-4" data-testid={testId}>
            <div className="fixed inset-0 bg-gray-700 opacity-40" onClick={onClose} />
            <div className="relative w-full max-w-5xl bg-white rounded-lg shadow-xl z-[61] max-h-[90vh] flex flex-col">
                <div className="flex items-start justify-between p-5 pb-2 shrink-0">
                    <div>
                        <h3 className="text-lg font-bold">Edit Payments</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {paymentsCount} payment{paymentsCount === 1 ? '' : 's'} across {posCount} PO{posCount === 1 ? '' : 's'}. Only changed payments are saved.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
                {blockedMessage && (
                    <div className="mx-5 mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        {blockedMessage}
                    </div>
                )}
                <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-3 space-y-4">
                    {selectedPOs.map((po) => {
                        const poRows = rows.filter((row) => row.poId === po.id);
                        return (
                            <div key={po.id} className="rounded-lg border border-gray-200">
                                <div className="flex items-center gap-2.5 px-3 py-2 bg-gray-50 rounded-t-lg">
                                    <span className="font-bold text-[15px] whitespace-nowrap">{po.poNumber}</span>
                                    <span className="text-xs text-gray-500 truncate">{po.vendor}</span>
                                    <span className={`ml-auto text-[13px] whitespace-nowrap ${getTotalOwedTextColor(po.amountOwed)}`}>
                                        Owed: ${formatPrice(po.amountOwed)}
                                    </span>
                                </div>
                                {poRows.length === 0 ? (
                                    <p className="px-3 py-3 text-sm text-gray-400">
                                        {po.status !== OPEN
                                            ? `${enumStatuses[po.status] || po.status || 'Unknown'} — set this PO to Open to edit its payments.`
                                            : 'No payments recorded for this PO.'}
                                    </p>
                                ) : (
                                    <div className="divide-y divide-gray-100">
                                        {poRows.map((row) => (
                                            <div key={row.paymentId} className={`px-3 py-2.5 ${rowIsDirty(row) ? 'bg-yellow-50' : ''}`}>
                                                {/* Desktop inline row */}
                                                <div className="hidden md:flex items-center gap-2">
                                                    {renderRowFields(row, false)}
                                                </div>
                                                {/* Mobile stacked fields */}
                                                <div className="md:hidden grid grid-cols-2 gap-2">
                                                    {renderRowFields(row, true)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                <div className="flex items-center justify-between gap-2 p-5 pt-3 border-t border-gray-100 shrink-0">
                    <span className="text-sm text-gray-500">
                        {dirtyRows.length} change{dirtyRows.length === 1 ? '' : 's'} pending
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveClick}
                            className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-white hover:bg-gray-800 disabled:opacity-50"
                            disabled={dirtyRows.length === 0}
                        >
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
